import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { Module, MutationTree, GetterTree, ActionTree } from 'vuex';
import { RootState } from '@core/interfaces/root-state';
import { ConfigState } from '@core/interfaces/config-state';
import { getProp, setProp } from '@shared/helpers/operators';
import store from '@app/store';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { bpcApi } from '@core/services/bpc-api.service';
import { Observable } from 'rxjs';

const bpcRouterHelper = new BPCApiRouteHelper();

// Initial state
const state: ConfigState = {
  config: {},
  configObservables: {},
  entityTypes: [],
  loading: {},
  loadingEntityTypes: false,
};

// Synchronous functions to get data from state (can be composed properties)
const getters: GetterTree<ConfigState, RootState> = {
  isLoading: state => (entityConfigName: string) => {
    return !!getProp(state.loading, entityConfigName);
  },
  isLoadingEntityTypes(state): boolean {
    return !!state.loadingEntityTypes;
  },
  entityConfig: state => (config: string) => {
    return getProp(state.config, config);
  },
  entityTypes: state => (config: string) => {
    return getProp(state, config);
  },
  configObservables: state => (entityConfigName: string): Observable<BPCGenericApiResponse> => {
    return state.configObservables[entityConfigName] || null;
  },
};

// Asynchornous functions to get data usually from an api call
const actions: ActionTree<ConfigState, RootState> = {
  // Get entity config
  getEntityConfig({ commit, getters }, payload: { config: string; path: string }): Promise<any> {
    const entityConfigName = payload['config'].split('.')[0];
    commit('startLoading', { entityConfigName: entityConfigName });

    return new Promise((resolve, reject) => {
      const storedConfig = getters.entityConfig(entityConfigName);
      if (!(storedConfig === undefined)) {
        commit('stopLoading', { entityConfigName: entityConfigName });
        resolve({ data: getProp(state.config, payload['config']) });
      } else {
        const tenantId = store.getters['tenant/selectedTenantId'];
        const url = payload['path']
          ? payload['path']
          : bpcRouterHelper.getEntityConfigPath(tenantId, entityConfigName);

        let observable = getters.configObservables(entityConfigName);
        if (!observable) {
          observable = bpcApi.get(url);
          commit('setConfigObservable', {
            entityConfigName: entityConfigName,
            observable: observable,
          });
        }
        observable.subscribe(
          (res: BPCGenericApiResponse) => {
            commit('storeEntityConfig', { entity: entityConfigName, data: res.data });
            commit('stopLoading', { entityConfigName: entityConfigName });
            commit('clearConfigObservable', { entityConfigName: entityConfigName });
            resolve({ data: getters.entityConfig(payload['config']) });
          },
          error => {
            console.log(error);
            commit('stopLoading', { entityConfigName: entityConfigName });
            commit('clearConfigObservable', { entityConfigName: entityConfigName });
            reject(error);
          },
        );
      }
    });
  },

  getEntityTypesConfig({ commit, getters }, config: string): Promise<any> {
    commit('startLoadingEntityTypes');

    return new Promise((resolve, reject) => {
      const storedConfig = getters.entityTypes(config);
      if (!(storedConfig === undefined)) {
        commit('stopLoadingEntityTypes');
        resolve({ data: storedConfig });
      } else {
        const context = config.split('.')[1];
        bpcApi.get(bpcRouterHelper.getEntityTypesConfigPath(context)).subscribe(
          (res: BPCGenericApiResponse) => {
            commit('storeEntityTypes', {
              path: context ? config : 'entityTypes',
              data: res.data,
            });
            commit('stopLoadingEntityTypes');
            resolve({ data: getters.entityTypes(config) });
          },
          error => {
            console.log(error);
            commit('stopLoadingEntityTypes');
            reject(error);
          },
        );
      }
    });
  },
};

// The vuex way of changing a state property
export const mutations: MutationTree<ConfigState> = {
  stopLoading(state, payload) {
    state.loading[payload.entityConfigName] = false;
  },
  startLoading(state, payload) {
    state.loading[payload.entityConfigName] = true;
  },
  stopLoadingEntityTypes(state) {
    state.loadingEntityTypes = false;
  },
  startLoadingEntityTypes(state) {
    state.loadingEntityTypes = true;
  },
  storeEntityConfig(state, payload) {
    setProp(state.config, payload.entity, payload.data);
  },
  storeEntityTypes(state, payload) {
    setProp(state, payload.path, payload.data);
  },
  setConfigObservable(state, payload) {
    state.configObservables[payload.entityConfigName] = payload.observable;
  },
  clearConfigObservable(state, payload) {
    setProp(state.configObservables, payload.entityConfigName, null);
  },
};

export const ConfigStoreModule: Module<ConfigState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
